import type { ComponentProps } from "react";
import React from "react";
import { tv } from "tailwind-variants";
import type { VariantProps } from "tailwind-variants";

import { cn } from "@/lib/helpers/cn";

export type BadgeVariantProps = VariantProps<typeof badgeStyles>;

type BadgeProps = ComponentProps<"span"> & BadgeVariantProps;

export function Badge2({
  appearance,
  children,
  className,
  size,
  variant,
  ...badgeProps
}: BadgeProps) {
  const { body } = badgeStyles({ appearance, size, variant });

  return (
    <span className={cn(body(), className)} {...badgeProps}>
      {children}
    </span>
  );
}

export const badgeStyles = tv({
  compoundVariants: [
    // solid
    {
      appearance: "solid",
      class: { body: "tw:bg-fill-bright-purple-quinary tw:text-text-bright-purple-primary" },
      variant: "bright-purple",
    },
    {
      appearance: "solid",
      class: { body: "tw:bg-fill-quinary tw:text-text-secondary" },
      variant: "neutral",
    },
    {
      appearance: "solid",
      class: { body: "tw:bg-fill-error-primary tw:text-text-white" },
      variant: "error",
    },
    {
      appearance: "solid",
      class: {
        body: "tw:bg-fill-success-primary tw:text-text-white",
      },
      variant: "success",
    },
    {
      appearance: "solid",
      class: { body: "tw:bg-fill-warning-secondary tw:text-text-primary" },
      variant: "warning",
    },
    {
      appearance: "solid",
      class: { body: "tw:bg-fill-navy-primary tw:text-text-white" },
      variant: "navy",
    },
    {
      appearance: "solid",
      class: { body: "tw:bg-fill-orange-primary tw:text-text-white" },
      variant: "orange",
    },
    {
      appearance: "solid",
      class: {
        body: "tw:bg-fill-teal-quinary tw:text-text-teal-primary",
      },
      variant: "bright-green",
    },
    // outline
    {
      appearance: "outline",
      class: {
        body: "tw:border tw:border-stroke-bright-purple-secondary tw:bg-fill-white tw:text-text-bright-purple-primary",
      },
      variant: "bright-purple",
    },
    {
      appearance: "outline",
      class: {
        body: "tw:border tw:border-fill-quaternary tw:bg-transparent tw:text-text-secondary",
      },
      variant: "neutral",
    },
    {
      appearance: "outline",
      class: {
        body: "tw:border tw:border-fill-error-primary tw:bg-transparent tw:text-fill-error-primary",
      },
      variant: "error",
    },
    {
      appearance: "outline",
      class: {
        body: "tw:border tw:border-fill-success-primary tw:bg-transparent tw:text-fill-success-primary",
      },
      variant: "success",
    },
    {
      appearance: "outline",
      class: {
        body: "tw:border tw:border-stroke-warning-primary tw:bg-transparent tw:text-warning-primary",
      },
      variant: "warning",
    },
    {
      appearance: "outline",
      class: {
        body: "tw:border tw:border-stroke-navy-primary tw:bg-transparent tw:text-text-secondary",
      },
      variant: "navy",
    },
    {
      appearance: "outline",
      class: {
        body: "tw:border tw:border-stroke-orange-primary tw:bg-transparent tw:text-text-secondary",
      },
      variant: "orange",
    },
  ],
  defaultVariants: {
    appearance: "solid",
    size: "sm",
    variant: "neutral",
  },
  slots: {
    body: cn(
      "tw:inline tw:h-auto",
      "trim-both tw:rounded-xs tw:text-center tw:select-none",
      "tw:print:[print-color-adjust:exact]",
      "tw:my-auto",
    ),
  },
  variants: {
    appearance: {
      outline: {},
      solid: {
        body: "tw:border tw:border-transparent",
      },
    },
    size: {
      md: {
        body: "tw:px-1.5 tw:py-1.75 label-sm tw:font-semibold",
      },
      sm: {
        body: "tw:px-1.5 tw:py-1.5 label-xs",
      },
      xs: {
        body: "tw:p-1.25 label-xs tw:text-xs",
      },
    },
    variant: {
      "bright-green": {},
      "bright-purple": {},
      error: {},
      navy: {},
      neutral: {},
      orange: {},
      success: {},
      warning: {},
    },
  },
});
